/**
 * Alertas — wireado contra `/api/alerts`.
 *
 * Cuando estamos en /c/:slug/alertas, el hook filtra por ese cliente.
 * Cuando estamos en /alerts (no existe esa ruta hoy, queda por si vuelve),
 * muestra cross-cliente respetando el acceso del usuario.
 *
 * Mantiene el visual aprobado v2.1: cards con border-l por severity,
 * filter chips, Ack/Ack-all outline magenta.
 */
import { useState, useMemo } from "react";
import { useParams } from "@tanstack/react-router";
import { cn } from "@/lib/cn";
import { EmptyState } from "@/components/shared/empty-state";
import {
  useAlerts,
  useAckAlert,
  useAckAllAlerts,
  type AlertSeverity,
  type PanelAlert,
} from "@/hooks/use-alerts";
import { findClient } from "@/lib/clients";
import { getAlertKindMeta, looksHuman } from "@/lib/alert-kinds";
import { NotificationRecipients } from "@/components/alerts/notification-recipients";
import type { ClientSlug } from "@/types";

const SEVERITY_STYLE: Record<
  AlertSeverity,
  { border: string; badge: { bg: string; text: string; border: string }; label: string }
> = {
  critical: {
    border: "var(--error)",
    badge: {
      bg: "var(--error-bg)",
      text: "var(--error-text)",
      border: "var(--error-border)",
    },
    label: "Crítica",
  },
  high: {
    border: "var(--warning)",
    badge: {
      bg: "var(--warning-bg)",
      text: "var(--warning-text)",
      border: "var(--warning-border)",
    },
    label: "Alta",
  },
  medium: {
    border: "var(--info)",
    badge: {
      bg: "var(--info-bg)",
      text: "var(--info-text)",
      border: "var(--info-border)",
    },
    label: "Media",
  },
  low: {
    border: "var(--text-muted)",
    badge: {
      bg: "var(--surface-2)",
      text: "var(--text-muted)",
      border: "var(--border-default)",
    },
    label: "Baja",
  },
};

type FilterMode = "all" | "unack" | "acked";

function formatRelativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms) || ms < 0) return "ahora";
  const min = Math.floor(ms / 60_000);
  if (min < 1) return "hace unos segundos";
  if (min < 60) return `hace ${min}m`;
  const h = Math.floor(min / 60);
  if (h < 24) return `hace ${h}h ${min % 60}m`;
  const d = Math.floor(h / 24);
  return `hace ${d}d`;
}

export function AlertsPage() {
  const { clientSlug } = useParams({ strict: false }) as { clientSlug?: string };
  const scopedClient = findClient(clientSlug);
  const [filter, setFilter] = useState<FilterMode>("unack");

  const { data: alerts, isLoading } = useAlerts({
    clientSlug: scopedClient?.slug,
    unresolvedOnly: false,
    limit: 100,
  });
  const ackOne = useAckAlert();
  const ackAll = useAckAllAlerts();

  const list = alerts ?? [];
  const visible = useMemo(() => {
    if (filter === "unack") return list.filter((a) => !a.acknowledged);
    if (filter === "acked") return list.filter((a) => a.acknowledged);
    return list;
  }, [list, filter]);

  const criticalCount = list.filter(
    (a) => a.severity === "critical" && !a.acknowledged,
  ).length;
  const unackCount = list.filter((a) => !a.acknowledged).length;

  function handleAckAll() {
    const ids = list.filter((a) => !a.acknowledged).map((a) => a.id);
    if (ids.length === 0) return;
    ackAll.mutate({ alertIds: ids });
  }

  return (
    <div className="min-h-full">
      {/* Header */}
      <header
        className="px-7 pt-5 pb-4 border-b border-border-subtle sticky top-0 z-20"
        style={{
          background: "var(--surface-overlay)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
        }}
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <h1 className="text-3xl font-semibold tracking-tight text-text-primary">
              Alertas
            </h1>
            <p className="mt-1 text-sm text-text-muted tabular-nums">
              {scopedClient && (
                <>
                  <span className="font-sans text-text-secondary">
                    {scopedClient.fullName}
                  </span>{" "}
                  ·{" "}
                </>
              )}
              <span
                className={cn(
                  "font-mono",
                  criticalCount > 0 ? "text-error-text" : "text-text-secondary",
                )}
              >
                {criticalCount}
              </span>{" "}
              críticas ·{" "}
              <span className="font-mono text-text-secondary">{unackCount}</span> sin ackear
            </p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <div
              className="inline-flex p-[3px] rounded-full bg-surface-2 border border-default"
            >
              {(
                [
                  { id: "all", label: "Todas", count: list.length },
                  { id: "unack", label: "Sin ack", count: unackCount },
                  { id: "acked", label: "Ackeadas", count: list.length - unackCount },
                ] as Array<{ id: FilterMode; label: string; count: number }>
              ).map((f) => (
                <button
                  key={f.id}
                  onClick={() => setFilter(f.id)}
                  className={cn(
                    "h-7 px-3 rounded-full text-xs font-medium font-sans",
                    "inline-flex items-center gap-1.5 transition-colors duration-100",
                    filter === f.id
                      ? "bg-surface-1 text-text-primary shadow-sm"
                      : "text-text-secondary hover:text-text-primary",
                  )}
                >
                  {f.label}
                  <span className="font-mono tabular-nums text-2xs text-text-muted">
                    {f.count}
                  </span>
                </button>
              ))}
            </div>

            <button
              onClick={handleAckAll}
              disabled={unackCount === 0 || ackAll.isPending}
              className={cn(
                "h-9 px-3.5 rounded-lg text-sm font-medium font-sans",
                "inline-flex items-center gap-2",
                "border border-magenta text-magenta bg-transparent",
                "hover:bg-magenta-soft transition-colors duration-100",
                "disabled:opacity-50 disabled:cursor-not-allowed",
              )}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <span>{ackAll.isPending ? "Ackeando…" : "Ack all"}</span>
            </button>
          </div>
        </div>
      </header>

      {/* Body */}
      <div className="px-7 py-6">
        {scopedClient && (
          <NotificationRecipients clientSlug={scopedClient.slug as ClientSlug} />
        )}
        {isLoading ? (
          <div className="text-sm text-text-muted">Cargando alertas…</div>
        ) : visible.length === 0 ? (
          <EmptyState
            title={
              filter === "unack"
                ? "Sin alertas pendientes"
                : filter === "acked"
                  ? "Sin alertas ackeadas"
                  : "Sin alertas todavía"
            }
            description={
              filter === "unack"
                ? "Todas las alertas están ackeadas. Buen trabajo."
                : filter === "acked"
                  ? "Todavía no se ackeó ninguna alerta."
                  : "Cuando el bot detecte un spike, un SLA breach o un fallo de webhook, va a aparecer acá."
            }
          />
        ) : (
          <ul className="space-y-3">
            {visible.map((alert) => (
              <AlertCard
                key={alert.id}
                alert={alert}
                onAck={() => ackOne.mutate({ alertId: alert.id })}
                ackPending={ackOne.isPending && ackOne.variables?.alertId === alert.id}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function AlertCard({
  alert,
  onAck,
  ackPending,
}: {
  alert: PanelAlert;
  onAck: () => void;
  ackPending: boolean;
}) {
  const sev = SEVERITY_STYLE[alert.severity];
  const client = alert.clientSlug ? findClient(alert.clientSlug) : null;
  const clientColor = client?.accent ?? "var(--text-muted)";
  const clientLabel = client?.label ?? alert.clientSlug ?? "Global";
  const meta = getAlertKindMeta(alert.kind);
  const description = looksHuman(alert.message) ? alert.message : meta.description;

  return (
    <li
      className={cn(
        "card-surface p-4 transition-opacity",
        alert.acknowledged && "opacity-70",
      )}
      style={{
        borderLeft: `3px solid ${sev.border}`,
      }}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <h3 className="text-base font-semibold text-text-primary truncate">
            {meta.label}
          </h3>
          <span
            className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-2xs font-sans font-medium bg-surface-2 border border-default text-text-secondary"
          >
            <span
              className="inline-block w-1.5 h-1.5 rounded-full"
              style={{ background: clientColor }}
            />
            {clientLabel}
          </span>
          <span
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-2xs font-sans font-semibold"
            style={{
              background: sev.badge.bg,
              color: sev.badge.text,
              border: `1px solid ${sev.badge.border}`,
            }}
          >
            {sev.label}
          </span>
          {alert.acknowledged && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-2xs font-sans font-medium bg-success-bg text-success-text border border-success-border"
            >
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              ack
            </span>
          )}
        </div>
      </div>

      <p className="mt-2 text-sm text-text-secondary">{description}</p>

      {meta.suggestedAction && !alert.acknowledged && (
        <p className="mt-2 text-xs text-text-muted">
          <span className="font-sans font-medium text-text-secondary">
            Sugerencia:
          </span>{" "}
          {meta.suggestedAction}
        </p>
      )}

      <div
        className="mt-3 flex items-center justify-between gap-3 flex-wrap pt-3"
        style={{ borderTop: "1px solid var(--border-subtle)" }}
      >
        <div className="flex items-center gap-3 text-2xs flex-wrap">
          <span
            className="font-mono tabular-nums text-text-muted"
            title={alert.createdAt}
          >
            {formatRelativeTime(alert.createdAt)}
          </span>
          <span className="text-text-disabled">·</span>
          <code
            className="font-mono text-text-disabled"
            title="Tipo técnico de la alerta (uso interno)"
          >
            {alert.kind}
          </code>
          {alert.actor && (
            <>
              <span className="text-text-disabled">·</span>
              <span className="font-mono text-text-secondary">{alert.actor}</span>
            </>
          )}
          {alert.resourceId && (
            <>
              <span className="text-text-disabled">·</span>
              <code className="font-mono tabular-nums text-text-secondary">
                {alert.resourceId}
              </code>
            </>
          )}
        </div>
        {!alert.acknowledged && (
          <button
            onClick={onAck}
            disabled={ackPending}
            className={cn(
              "h-8 px-3 rounded-lg text-xs font-medium font-sans",
              "inline-flex items-center gap-1.5",
              "border border-magenta text-magenta bg-transparent",
              "hover:bg-magenta-soft transition-colors duration-100",
              "disabled:opacity-50 disabled:cursor-not-allowed",
            )}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            {ackPending ? "Ackeando…" : "Ack"}
          </button>
        )}
      </div>
    </li>
  );
}
